import { CommandeBC, TabDescriptor, TabKind } from '../types';
import { canAccepterRefuser, canPrestataireEditCatalogue } from './commandeConsultation';

// Decisions PURES des CTA de navigation inter-onglets (case PRESTATAIRE x BC) : libelle + visibilite.
// Aucune dependance React.
//
// POURQUOI le domaine et pas le JSX : la regle client est transverse aux onglets — un seul bouton
// orange par onglet, masque si la destination est fermee OU si l'onglet propose deja une action qui
// fait avancer le workflow. Eclatee dans trois rendus, elle finit par diverger d'un onglet a l'autre
// (et le libelle, contractuel, se reformule). Ici elle se lit d'un bloc et se teste sans monter React.

export interface NextTabCta {
  label: string;
  /** Destination fermee, ou action orange deja due sur cet onglet : pas de second CTA. */
  hidden: boolean;
}

// Un onglet n'est une destination valide que si `goToTab` y menerait vraiment : meme predicat que
// le shell (SuiviDeCommandeBCV2.goToTab), sinon on afficherait un bouton qui ne fait rien.
export const isTabReachable = (
  descriptors: TabDescriptor[],
  kind: TabKind,
): boolean => descriptors.some((d) => d.kind === kind && !d.disabled && !d.inactive);

// Le presta peut-il encore DEPOSER son offre ? Vrai tant qu'une decision lui revient : accepter /
// refuser (panier normal) ou chiffrer son catalogue (panier inverse). Faux des qu'il a agi, que le
// delai est passe, ou que l'acheteur a tranche.
//
// UN seul predicat pour DEUX usages, parce que c'est le meme fait metier :
//   - Consultation : il decide du LIBELLE (« Déposer mon offre » tant que c'est deposable, sinon
//     « Voir l'offre ») ;
//   - Offre : il decide du MASQUAGE (deposable => l'onglet porte deja son orange « Accepter
//     l'operation » / « Envoyer mon offre a l'acheteur », c'est lui le CTA du moment).
// Les dedoubler laisserait les deux onglets diverger sur la meme question.
// `canAccepterRefuser` integre deja le « pas encore agi » (cf. commandeConsultation) : le redire ici
// serait une conjonction morte.
export const offreEstDeposable = (commande: CommandeBC, chronoExpired: boolean): boolean =>
  canAccepterRefuser(commande, chronoExpired) ||
  canPrestataireEditCatalogue(commande, chronoExpired);

// Consultation -> Offre. Rien a masquer au-dela de l'atteignabilite : la barre d'actions de la
// Consultation ne porte que le refus (rouge) et l'impression (secondaire), jamais un orange de
// workflow. Le CTA reste donc offert APRES le depot (statuts 7/8/9/13/15), ou il devient l'acces en
// lecture a l'offre — c'est justement au statut Valide(7) que la suite du parcours s'ouvre.
export const computeConsultationNextTabCta = (
  commande: CommandeBC,
  chronoExpired: boolean,
  offreReachable: boolean,
): NextTabCta => ({
  label: offreEstDeposable(commande, chronoExpired) ? 'Déposer mon offre' : "Voir l'offre",
  hidden: !offreReachable,
});

// Offre -> Notification et signature. Le telechargement du bon de commande signe, lui, ne fait rien
// avancer : il ne bloque pas le CTA.
export const computeOffreNextTabCta = (
  commande: CommandeBC,
  chronoExpired: boolean,
  notificationReachable: boolean,
): NextTabCta => ({
  label: 'Accéder au bon de commande signé',
  hidden: !notificationReachable || offreEstDeposable(commande, chronoExpired),
});

// Notification et signature -> Messagerie. Le seul orange de l'onglet est un telechargement : rien
// a arbitrer au-dela de l'ouverture de la destination.
export const computeNotificationNextTabCta = (messagerieReachable: boolean): NextTabCta => ({
  label: 'Aller dans la messagerie',
  hidden: !messagerieReachable,
});

// Messagerie -> Evaluation. Cote presta, c'est l'acheteur qui est evalue.
export const computeMessagerieNextTabCta = (evaluationReachable: boolean): NextTabCta => ({
  label: "Évaluer l'acheteur",
  hidden: !evaluationReachable,
});
